import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useAICoach } from './AICoachContext';
import { Button, ChatMessageView, Checkbox, Icon, Loading, Select } from '@aidkitorg/component-library';
import { useMarkdown } from '../Util';
import { ChatConversationItem } from './ChatChannel';
import { useLocalizedStrings } from '../Localization';
import { useAsyncEffect } from '../Hooks/AsyncEffect';
import { useApplicant } from '../Contexts/ApplicantContext';

const AGENT_NAME = "Opal";

export function AICoachButton(props: { onClick: () => void }) {
  const L = useLocalizedStrings();

  return <div className="z-60">
    <Button
      onClick={() => props.onClick()}
      style={
        {
          borderRadius: "4rem"
        }
      }
      aria-label={L.ai_coach.ask_your_ai_coach.replace("$name", AGENT_NAME)}
    >
      <span className="text-xs flex items-center gap-1">
        <Icon name="ChatBubbleLeftRightIcon" role="decorative" className="text-white" size="sm" />
        <span className="hidden lg:block">{L.ai_coach.ask_name.replace("$name", AGENT_NAME)}</span>
      </span>
    </Button>
  </div>
}

function AudioInputSelector(props: { 
  onSelectDevice: (result: { device: MediaStream | null, remember: boolean }) => void,
  onError: (err: any) => void
}) {
  const L = useLocalizedStrings();
  const [availableDeviceInfos, setAvailableDeviceInfos] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceInfo, setSelectedDeviceInfo] = useState<MediaDeviceInfo | null>(null);
  const [remember, setRemember] = useState<boolean>(true);
  const [selectedOption, setSelectedOption] = useState<{ value: string, label: string } | null>(null);
  const options = useMemo(
    () => availableDeviceInfos.map(d => ({ value: d.deviceId, label: d.label })),
    [availableDeviceInfos]
  );

  // load available audio input device
  useAsyncEffect(async () => {
    let availableDevices = [];
    try {
      const d = await navigator.mediaDevices.getUserMedia({ audio: true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      availableDevices = devices.filter(d => d.kind === "audioinput");
      setAvailableDeviceInfos(availableDevices);
      // devices were only accessed to populate the list
      // they can now be stopped
      d.getTracks().forEach(track => track.stop());
    }
    finally {
      if (!availableDevices.length) {
        props.onError("Failed to access audio input devices");
      }
    }  
  }, []);

  // user has made their selection, get the MediaStream for that device
  useAsyncEffect(async () => {
    if (selectedDeviceInfo) {
      try {
        const device = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: { exact: selectedDeviceInfo.deviceId }
          }        
        });
        if (!device) {
          throw new Error(`Could not find device for device id ${selectedDeviceInfo.deviceId}`);
        }
        // success callback
        props.onSelectDevice({
          device,
          remember
        });
      }
      catch(err) {
        console.error(err);
        props.onError("Failed to get device");
      }
    }
  }, [selectedDeviceInfo]);

  // set default selected option
  useEffect(() => {
    if (options.length && !selectedOption) {
      setSelectedOption(options[0]);
    }
  }, [selectedOption, options]);

  if (!options.length) {
    return <Loading />
  }

  return <form className="flex flex-col gap-2" onSubmit={e => {
    e.preventDefault();   
    const selectedDeviceInfo = availableDeviceInfos.find(d => d.deviceId === selectedOption?.value) ?? null;    
    if (selectedDeviceInfo) {
      setSelectedDeviceInfo(selectedDeviceInfo);
    }
  }}>
    <div>
      <Select        
        label={L.ai_coach.microphone}
        labelLocation="above"
        description={L.ai_coach.select_microphone}
        value={selectedOption || options[0]}
        onChange={option => setSelectedOption(option as typeof selectedOption)}
        options={options} />
    </div>
    <div className="flex gap-2 items-center">
      <Checkbox
        label={L.ai_coach.remember}
        checked={remember}
        onChange={v => setRemember(v as boolean)}
      />
      <Button 
        className="grow"
        type="submit"
        icon={<Icon name="CheckCircleIcon" type="solid" size="xl" />}
        isDisabled={!options.length}>        
      </Button>
    </div>
  </form>
}

export function AICoachChatWindow(props: { onClickMinimize: () => void }) {
  const L = useLocalizedStrings();
  const [inputText, setInputText] = useState<string>("");
  const [showAudioInputSelection, setShowAudioInputSelection] = useState<boolean>(false);
  const [remember, setRemember] = useState<boolean>(false);
  const [failedToGetAudioInput, setFailedToGetAudioInput] = useState<boolean>(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const {
    transcript,
    hasAcceptedTerms,
    setHasAcceptedTerms,
    active,
    modality,
    setModality,
    audioInputStream,
    startAudioInput,
    setAudioElement,
    chatChannel
  } = useAICoach();
  const { info } = useApplicant();

  useEffect(() => { console.log('INFO', info) }, [info]);

  const handleChatSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    if (inputText) {
      chatChannel?.sendChatMessage(inputText, { role: 'user', createResponse: true, allowAudio: false });
    }
    setInputText('');
  }

  function MessageContent({ message }: { message: ChatConversationItem }) {
    const content = message.content?.map(c => c.text ?? c.transcript).join('');
    return useMarkdown(content);
  }

  const messageBubbles: Parameters<typeof ChatMessageView>[0]['transcript'] = useMemo(() => {
    let items: Parameters<typeof ChatMessageView>[0]['transcript'] = [];

    items = items.concat(transcript.filter(t => true || !t.meta?.hidden).map(m => ({
      role: m.role,
      meta: m.meta,
      content: <MessageContent message={m} />
    })));

    return items;
  }, [transcript]);
  
  /* Sliding chat panel - full screen on mobile, partial on desktop */
  return <div className="fixed bottom-0 right-0 z-50 w-full sm:w-80 md:w-[360px] lg:w-[450px] max-w-[100vw] sm:max-w-[90vw] 
                      h-full sm:h-[90vh] md:h-[75vh] bg-white shadow-lg flex flex-col sm:rounded-t-lg">
    {/* Header */}
    <div className="border-b border-gray-200">
      <div className="flex flex-col p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <img src="https://i.postimg.cc/GmczHr3p/avatar-opal.png" alt={AGENT_NAME} className="w-12 h-12 rounded-full mr-3" />
            <h3 className="text-2xl font-semibold mt-2">{L.ai_coach.chat_with.replace('$name', AGENT_NAME)}</h3>
          </div>
          {/* Minimize button */}
          <div className="group relative">
            <Button 
              variant="secondary"
              className="p-2"
              onClick={props.onClickMinimize}
              aria-label={L.ai_coach.minimize_chat}
            >
              <Icon name="ArrowsPointingInIcon" />
              <div className="absolute bottom-full right-0 mb-1 px-2 py-1 bg-gray-800 text-white text-xs rounded pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                {L.ai_coach.minimize_chat}
              </div>
            </Button>
          </div>
        </div>
      </div>
    </div>

    {/* Chat content */}
    <ChatMessageView
      transcript={messageBubbles}
      roles={{
        assistant: {
          side: 'left',
          color: 'bg-indigo-100',
          icon: <img src="https://i.postimg.cc/GmczHr3p/avatar-opal.png" alt="Opal" className="rounded-full" />
        },
        user: {
          side: 'right',
          icon: <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center font-semibold text-white">{info.legal_name?.substring(0, 2) || 'U'}</div>,
          color: 'bg-gray-100',
        }
      }}
      autoScroll={true}
    />

    {/* Terms of Service - shown only before acceptance */}
    {!hasAcceptedTerms && (
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <h4 className="font-semibold text-gray-800 mb-2">{L.terms_and_conditions}</h4>
        <p className="text-sm text-gray-600 mb-3">
          {L.ai_coach.terms_full.replaceAll("$name", AGENT_NAME)}
        </p>
        <Button 
          onClick={() => setHasAcceptedTerms(true)}
          className="w-full">
          {L.ai_coach.accept_terms}
        </Button>
      </div>
    )}

    {(showAudioInputSelection && (!audioInputStream || !remember) && !failedToGetAudioInput) && <div className="border-t bg-gray-50 border-gray-200 p-3">
      <AudioInputSelector 
        onSelectDevice={
          ({device, remember}) => {
            if (device) {
              startAudioInput(device);
              setFailedToGetAudioInput(false);
              setShowAudioInputSelection(false);
            }
            setRemember(remember);
          }
        }
        onError={
          () => setFailedToGetAudioInput(true)
        } />
    </div>}

    {/* Message input - only shown after terms acceptance */}
    {hasAcceptedTerms && (
      <div className="border-t border-gray-200 p-3">
        <div className="flex items-center gap-2">
          {/* Microphone button */}
          <Button
            variant={(modality === "audio" && audioInputStream) ? "primary" : "secondary"}
            shape="circle"
            isDisabled={failedToGetAudioInput}
            aria-label={L.ai_coach.voice_input}
            onClick={function onClickMicrophoneIcon(){     
              // toggle modality
              if (modality === "text") {
                setModality("audio");
                setAudioElement(audioRef.current);
                setShowAudioInputSelection(true);
              }
              else {
                setModality("text")
                setAudioElement(null);
                setShowAudioInputSelection(false);
              }
            }}
          >
            <Icon name="MicrophoneIcon" />
          </Button>

          <form
            className="flex flex-1 gap-2"
            onSubmit={handleChatSubmit}>
            <input
              type="text"
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder={`${L.ai_coach.type_a_message}...`}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
            />
            <Button
              type="submit"
              isDisabled={!inputText || !active}
            >
              <Icon
                className="-rotate-45 -mt-1 -mr-1"
                name="PaperAirplaneIcon" />
            </Button>
          </form>
        </div>
      </div>
    )}
    <audio ref={audioRef} muted={modality !== "audio"} hidden autoPlay={true} />
  </div>
}
